import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material'
import { FormInput } from '@/components/form'
import { getFPPasswordSchema, IHttpError, IUser, User } from '@/@data'
import { useTranslator } from '@/hooks/useTranslator'
import { useNotification } from '@/hooks/useNotification'
import { yupResolver } from '@hookform/resolvers/yup'

interface ResetPasswordFormProps {
  employee?: IUser
  onSuccess?: () => void
}

const ResetPasswordForm = React.forwardRef<{ submit: () => void } | null, ResetPasswordFormProps>(
  ({ employee, onSuccess }, ref) => {
    const { __ } = useTranslator()
    const n = useNotification()

    const [showPassword, setShowPassword] = useState(false)
    const [showPasswordConfirm, setShowConfirmPassword] = useState(false)

    const handleClickShowPassword = () => setShowPassword(show => !show)

    const { useUpdateMutation } = User.objects.getCommonMutations()
    const updateUserMutation = useUpdateMutation()

    const methods = useForm<any>({
      defaultValues: {
        password: employee?.password || '',
        confirm_password: employee?.password || '',
        role_id: employee?.role_id || 0,
        phone: employee?.phone || '',
        employee_id: employee?.employee_id || ''
      },
      resolver: yupResolver(getFPPasswordSchema(__)),
      mode: 'onChange'
    })

    const onSubmit = (data: IUser) => {
      if (!employee) return
      console.log('onSubmit called', data)

      const payload = {
        ...data
      }

      console.log('Submitting payload:', payload)

      updateUserMutation.mutate(
        { id: employee.id, data: payload },
        {
          onSuccess: response => {
            n.success(response.message)
            onSuccess?.()
          },
          onError: (error: IHttpError) => {
            n.error(error.message)
          }
        }
      )
    }

    React.useImperativeHandle(ref, () => ({
      submit: () => {
        console.log('Imperative submit called')

        methods.handleSubmit(onSubmit)()
      }
    }))

    useEffect(() => {
      if (!employee) return

      methods.reset({
        password: '',
        confirm_password: '',
        role_id: employee.role_id || 0,
        phone: employee.phone || '',
        employee_id: employee.employee_id || ''
      })
    }, [employee, methods])

    const password = methods.watch('password') || ''
    const passwordConfirm = methods.watch('confirm_password') || ''

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box className='flex flex-col gap-4 '>
            <Grid container spacing={4}>
              <Grid size={6}>
                <FormInput
                  className='rounded-full w-full'
                  type={showPassword ? 'text' : 'password'}
                  id='outlined-adornment-password'
                  placeholder={__('Password')}
                  name='password'
                  control={methods.control}
                  errorMessage={methods.formState.errors.password?.message?.toString()}
                  slotProps={{
                    input: {
                      style: { padding: '5px 15px', borderRadius: '50px' },
                      startAdornment: (
                        <InputAdornment position='start'>
                          <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          {methods.watch('password') && (
                            <IconButton size='small' onClick={() => methods.setValue('password', '')} tabIndex={-1}>
                              <i className='tabler-circle-x' />
                            </IconButton>
                          )}
                          <IconButton
                            edge='end'
                            onClick={handleClickShowPassword}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <i className={showPassword ? 'tabler-eye-off' : 'tabler-eye'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormInput
                  className='rounded-full w-full'
                  type={showPasswordConfirm ? 'text' : 'password'}
                  id='outlined-adornment-password-confirm'
                  placeholder={__('Confirm Password')}
                  name='confirm_password'
                  control={methods.control}
                  errorMessage={methods.formState.errors.confirm_password?.message?.toString()}
                  slotProps={{
                    input: {
                      style: { padding: '5px 15px', borderRadius: '50px' },
                      startAdornment: (
                        <InputAdornment position='start'>
                          <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          {passwordConfirm !== '' && (
                            <IconButton
                              size='small'
                              onClick={() => methods.setValue('confirm_password', '')}
                              tabIndex={-1}
                            >
                              <i className='tabler-circle-x' />
                            </IconButton>
                          )}
                          <IconButton
                            edge='end'
                            onClick={() => setShowConfirmPassword(show => !show)}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <i className={showPasswordConfirm ? 'tabler-eye-off' : 'tabler-eye'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box className='flex flex-col gap-2 mb-4'>
              <Box display='flex' alignItems='center' gap={1}>
                <Box
                  className='w-[10px] h-[10px] rounded-full '
                  bgcolor={/[0-9]/.test(password) ? '#197049' : '#e0e0e0'}
                />
                <Typography
                  className='text-[13px] leading-[20px]'
                  color={/[0-9]/.test(password) ? '#197049' : '#4b465c'}
                >
                  {__('At least one numeral (0-9)')}
                </Typography>
              </Box>
              <Box display='flex' alignItems='center' gap={1}>
                <Box
                  className='w-[10px] h-[10px] rounded-full '
                  bgcolor={password.length >= 6 ? '#197049' : '#e0e0e0'}
                />
                <Typography
                  className='text-[13px]  leading-[20px]'
                  color={password.length >= 6 ? '#197049' : '#4b465c'}
                >
                  {__('Minimum 6 characters')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </form>
      </FormProvider>
    )
  }
)

export default ResetPasswordForm
